import { safeFetch } from "../utils/safeFetch";
import { validateSpaReport } from "../utils/spaReports";

const MOCK_DELAY_MS = 180;

const wait = (ms = MOCK_DELAY_MS) => new Promise((resolve) => setTimeout(resolve, ms));

const spaAuthHeaders = (token, tenant) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
  "x-tenant-id": tenant,
  "Cache-Control": "no-cache, no-store, must-revalidate",
  Pragma: "no-cache",
  Expires: "0",
});

const readErrorPayload = async (response) => {
  const fallback = `HTTP error! status: ${response.status}`;
  try {
    const payload = await response.json();
    const issues = payload?.issues
      ? (typeof payload.issues === "string" ? payload.issues : JSON.stringify(payload.issues))
      : "";
    return {
      message: [payload?.error || payload?.message || fallback, issues].filter(Boolean).join(": "),
      rejections: payload?.rejections || [],
      payload,
    };
  } catch (_) {
    try {
      const text = await response.text();
      return { message: text || fallback, rejections: [], payload: null };
    } catch (_unusedError) {
      return { message: fallback, rejections: [], payload: null };
    }
  }
};

const spaRequest = async (path, token, tenant, options = {}) => {
  const { onlineOnly = false, responseType, ...fetchOptions } = options;
  const headers = { ...spaAuthHeaders(token, tenant), ...(options.headers || {}) };
  if (options.body instanceof FormData) delete headers['Content-Type'];
  // Time-sensitive waitlist decisions must never be replayed from the offline queue.
  const response = await (onlineOnly ? fetch : safeFetch)(`/api/spa${path}`, {
    ...fetchOptions,
    headers,
    cache: "no-store",
  });

  if (!response.ok) {
    const errorPayload = await readErrorPayload(response);
    const error = new Error(errorPayload.message);
    error.status = response.status;
    error.rejections = errorPayload.rejections;
    error.payload = errorPayload.payload;
    throw error;
  }

  if (responseType === 'pdf') {
    if (!response.headers.get('content-type')?.toLowerCase().includes('application/pdf')) throw new Error('spa.clinical.invalidResponse');
    return response.blob();
  }
  return response.status === 204 ? null : response.json();
};

export async function getSpaOperationalReport(range, token, tenant, options = {}) {
  const data = await spaRequest(`/reports/operational${queryString(range)}`, token, tenant, {
    onlineOnly: true, signal: options.signal,
  });
  return validateSpaReport(data, 'operational', range);
}

// Clinical requests must never enter offline storage or replay queues.
export function getSpaClinicalRecord(clientId, token, tenant, options = {}) {
  return spaRequest(`/clients/${clientId}/clinical-record`, token, tenant, { onlineOnly: true, signal: options.signal });
}
export function updateSpaClinicalRecord(clientId, patch, token, tenant) {
  return spaRequest(`/clients/${clientId}/clinical-record`, token, tenant, { onlineOnly: true, method: 'PATCH', body: JSON.stringify(patch) });
}
export function getSpaClinicalSection(clientId, section, token, tenant, options = {}) {
  if (!['history', 'consents', 'documents'].includes(section)) return Promise.reject(new Error('Invalid clinical section'));
  return spaRequest(`/clients/${clientId}/clinical-record/${section}`, token, tenant, { onlineOnly: true, signal: options.signal });
}
export function getSpaClinicalProfessionals(token, tenant, options = {}) {
  return spaRequest('/operators', token, tenant, { onlineOnly: true, signal: options.signal });
}

export function clinicalArtifactRequest(clientId, path, token, tenant, options = {}) {
  if (!Number.isSafeInteger(Number(clientId)) || Number(clientId) < 1
    || !/^(consents(?:\/[1-9]\d*(?:\/(?:sign|revoke))?)?|documents(?:\/[1-9]\d*(?:\/(?:download|file))?)?|access-log)$/.test(path)) {
    return Promise.reject(new Error('spa.clinical.invalidResponse'));
  }
  const { payload, filters, ...rest } = options;
  return spaRequest(`/clients/${clientId}/clinical-record/${path}${queryString(filters || {})}`, token, tenant, {
    ...rest, onlineOnly: true, ...(payload !== undefined ? { body: payload instanceof FormData ? payload : JSON.stringify(payload) } : {}),
  });
}

export async function getSpaFinancialReport(range, token, tenant, options = {}) {
  const data = await spaRequest(`/reports/financial${queryString(range)}`, token, tenant, {
    onlineOnly: true, signal: options.signal,
  });
  return validateSpaReport(data, 'financial', range);
}

export async function getSpaCommissionRules(filters = {}, token, tenant, options = {}) {
  const data = await spaRequest(`/commissions/rules${queryString(filters)}`, token, tenant, {
    onlineOnly: true,
    signal: options.signal,
  });
  return Array.isArray(data) ? data : [];
}

export function createSpaCommissionRule(payload, token, tenant) {
  return spaRequest('/commissions/rules', token, tenant, {
    onlineOnly: true,
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getSpaCommissionLedger(filters = {}, token, tenant, options = {}) {
  const data = await spaRequest(`/commissions/ledger${queryString(filters)}`, token, tenant, {
    onlineOnly: true,
    signal: options.signal,
  });
  return Array.isArray(data) ? data : [];
}

export function getSpaCommissionReport(filters, token, tenant, options = {}) {
  return spaRequest(`/commissions/report${queryString({ ...filters, format: 'json' })}`, token, tenant, {
    onlineOnly: true,
    signal: options.signal,
  });
}

export async function downloadSpaCommissionReport(filters, token, tenant, options = {}) {
  const response = await fetch(`/api/spa/commissions/report${queryString({ ...filters, format: 'csv' })}`, {
    headers: spaAuthHeaders(token, tenant),
    cache: 'no-store',
    signal: options.signal,
  });
  if (!response.ok) {
    const errorPayload = await readErrorPayload(response);
    const error = new Error(errorPayload.message);
    error.status = response.status;
    error.payload = errorPayload.payload;
    throw error;
  }
  const disposition = response.headers?.get?.('content-disposition') || '';
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]
    || `operator-compensation-${filters.from}-${filters.to}.csv`;
  return { blob: await response.blob(), filename };
}

export function getSpaOperatorActivity(operatorId, range, token, tenant, options = {}) {
  return spaRequest(`/operators/${operatorId}/activity${queryString(range)}`, token, tenant, {
    onlineOnly: true,
    signal: options.signal,
  });
}

export function getSpaWaitlist(filters = {}, token, tenant) {
  return spaRequest(`/waitlist${queryString(filters)}`, token, tenant, { onlineOnly: true });
}

export function createSpaWaitlistRequest(payload, token, tenant) {
  return spaRequest('/waitlist', token, tenant, {
    onlineOnly: true, method: 'POST', body: JSON.stringify(payload),
  });
}

export function updateSpaWaitlistRequest(id, payload, token, tenant) {
  return spaRequest(`/waitlist/${id}`, token, tenant, {
    onlineOnly: true, method: 'PATCH', body: JSON.stringify(payload),
  });
}

export function cancelSpaWaitlistRequest(id, token, tenant) {
  return spaRequest(`/waitlist/${id}/cancel`, token, tenant, { onlineOnly: true, method: 'PATCH' });
}

export function getSpaWaitlistOffers(filters = { status: 'ACTIVE' }, token, tenant, options = {}) {
  return spaRequest(`/waitlist/offers${queryString(filters)}`, token, tenant, { onlineOnly: true, signal: options.signal });
}

export function acceptSpaWaitlistOffer(id, payload, token, tenant) {
  return spaRequest(`/waitlist/offers/${id}/accept`, token, tenant, {
    onlineOnly: true, method: 'PATCH', body: JSON.stringify(payload),
  });
}

export function declineSpaWaitlistOffer(id, token, tenant) {
  return spaRequest(`/waitlist/offers/${id}/decline`, token, tenant, { onlineOnly: true, method: 'PATCH' });
}

export function getSpaCommunicationPreferences(clientId, token, tenant) {
  return spaRequest(`/clients/${clientId}/communication-preferences`, token, tenant, { onlineOnly: true });
}

export function updateSpaCommunicationPreferences(clientId, payload, token, tenant) {
  return spaRequest(`/clients/${clientId}/communication-preferences`, token, tenant, {
    onlineOnly: true, method: 'PATCH', body: JSON.stringify(payload),
  });
}

export function getSpaCommunicationTemplates(filters, token, tenant, options = {}) {
  return spaRequest(`/communication-templates${queryString(filters)}`, token, tenant, {
    onlineOnly: true, signal: options.signal,
  });
}

export function saveSpaCommunicationTemplate(payload, token, tenant) {
  return spaRequest('/communication-templates', token, tenant, {
    onlineOnly: true, method: 'PUT', body: JSON.stringify(payload),
  });
}

export function getSpaAppointmentCommunications(appointmentId, token, tenant, options = {}) {
  return spaRequest(`/appointments/${appointmentId}/communications`, token, tenant, {
    onlineOnly: true, signal: options.signal,
  });
}

export function getSpaCommunicationCapabilities(token, tenant, options = {}) {
  return spaRequest('/communications/capabilities', token, tenant, { onlineOnly: true, signal: options.signal });
}

export function getSpaCommunicationRules(token, tenant, options = {}) {
  return spaRequest('/communications/rules', token, tenant, { onlineOnly: true, signal: options.signal });
}

export function updateSpaCommunicationRules(payload, token, tenant) {
  return spaRequest('/communications/rules', token, tenant, { onlineOnly: true, method: 'PATCH', body: JSON.stringify(payload) });
}

export function getSpaCommunicationHistory(filters, token, tenant, options = {}) {
  return spaRequest(`/communications${queryString(filters)}`, token, tenant, { onlineOnly: true, signal: options.signal });
}

export function sendSpaCommunication(payload, token, tenant) {
  return spaRequest('/communications/send', token, tenant, { onlineOnly: true, method: 'POST', body: JSON.stringify(payload) });
}

export function resendSpaCommunication(id, token, tenant) {
  return spaRequest(`/communications/${id}/resend`, token, tenant, { onlineOnly: true, method: 'POST' });
}

export function getSpaCommunicationTargetRecords(type, token, tenant, options = {}) {
  const paths = { appointmentId: '/appointments', waitlistOfferId: '/waitlist/offers?status=ACTIVE',
    storedValueInstrumentId: '/stored-value-instruments', packageEntitlementId: '/package-entitlements' };
  if (!paths[type]) return Promise.reject(new Error('Invalid communication target type'));
  return spaRequest(paths[type], token, tenant, { onlineOnly: true, signal: options.signal });
}

const pad = (value) => String(value).padStart(2, "0");

const displayPersonName = (person, fallback = "-") => {
  const fullName = [person?.name, person?.surname].filter(Boolean).join(" ").trim();
  return fullName || person?.email || fallback;
};

const toLocalDateParts = (value) => {
  if (!value) return { date: "", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: "", time: "" };
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

export const localDateTimeWithOffset = (date = new Date()) => {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteOffset = Math.abs(offsetMinutes);
  const offset = `${sign}${pad(Math.floor(absoluteOffset / 60))}:${pad(absoluteOffset % 60)}`;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${offset}`;
};

export const dateTimeWithLocalOffset = (dateKey, timeValue) => {
  const [hours = "00", minutes = "00", seconds = "00"] = String(timeValue || "00:00").split(":");
  const date = new Date(
    Number(dateKey.slice(0, 4)),
    Number(dateKey.slice(5, 7)) - 1,
    Number(dateKey.slice(8, 10)),
    Number(hours),
    Number(minutes),
    Number(seconds),
    0
  );
  return localDateTimeWithOffset(date);
};

export const nowWithLocalOffset = () => localDateTimeWithOffset(new Date());

export const spaDisplayName = (item, fallbackPrefix = "Item") => {
  const fullName = [item?.name, item?.surname].filter(Boolean).join(" ").trim();
  return fullName || item?.displayName || item?.email || `${fallbackPrefix} ${item?.id ?? ""}`.trim();
};

const firstCategoryLabel = (categories = []) => {
  const first = Array.isArray(categories) ? categories[0] : null;
  if (!first) return "-";
  return [first.categoryName, first.subcategoryName].filter(Boolean).join(" - ") || "-";
};

export const mapSpaTreatment = (treatment) => ({
  ...treatment,
  id: Number(treatment.id),
  name: treatment.name || `Treatment ${treatment.id}`,
  durationMinutes: Number(treatment.durationMinutes || 0),
  preparationMinutes: Number(treatment.preparationMinutes || 0),
  cleanupMinutes: Number(treatment.cleanupMinutes || 0),
  slotDurationMinutes: Number(treatment.slotDurationMinutes || treatment.durationMinutes || 0),
  price: Number(treatment.price || 0),
  category: firstCategoryLabel(treatment.categories),
  categoryId: Array.isArray(treatment.categories) && treatment.categories[0]?.categoryId
    ? Number(treatment.categories[0].categoryId)
    : "",
  subcategoryId: Array.isArray(treatment.categories) && treatment.categories[0]?.subcategoryId
    ? Number(treatment.categories[0].subcategoryId)
    : "",
  vatRate: Number(
    treatment.fiscalRule?.vatRate
    ?? (Array.isArray(treatment.categories) && treatment.categories[0]?.vatRate)
    ?? 0
  ),
  active: treatment.active !== false,
});

export const mapSpaOperator = (operator) => {
  const rawName = operator?.name || "";
  const displayName = spaDisplayName(operator, "Operator");
  return {
    ...operator,
    id: Number(operator.id),
    name: rawName || displayName,
    displayName,
    skills: Array.isArray(operator.specialties) ? operator.specialties : [],
    active: operator.active !== false,
    email: operator.email || "",
    engagementReminderMinutes: Number(operator.engagementReminderMinutes || 60),
  };
};

export const mapSpaCabin = (cabin) => ({
  ...cabin,
  id: Number(cabin.id ?? cabin.roomId),
  roomId: Number(cabin.roomId ?? cabin.id),
  name: cabin.name || `Cabin ${cabin.id ?? cabin.roomId}`,
  area: cabin.cabinType || "SPA",
  capacity: Number(cabin.capacity || 1),
  status: cabin.active === false ? "out_of_service" : "available",
  active: cabin.active !== false,
  characteristics: Array.isArray(cabin.characteristics) ? cabin.characteristics : [],
  equipment: Array.isArray(cabin.equipment) ? cabin.equipment : [],
  compatibleTreatments: Array.isArray(cabin.compatibleTreatments) ? cabin.compatibleTreatments : [],
  blocks: Array.isArray(cabin.blocks) ? cabin.blocks : [],
  statusIntervals: Array.isArray(cabin.statusIntervals) ? cabin.statusIntervals : [],
  appointments: Array.isArray(cabin.appointments) ? cabin.appointments : [],
});

export const mapSpaEquipment = (equipment) => ({
  ...equipment,
  id: Number(equipment.id),
  area: Array.isArray(equipment.placements) && equipment.placements.length
    ? equipment.placements.map((placement) => `Cabin ${placement.roomId} (${placement.quantity})`).join(", ")
    : "-",
  quantity: Number(equipment.quantity || 0),
  status: equipment.active === false ? "out_of_service" : "available",
  active: equipment.active !== false,
});

export const mapSpaAppointment = (appointment = {}) => {
  const start = toLocalDateParts(appointment.startAt);
  const end = toLocalDateParts(appointment.endAt);
  const assigned = appointment.assignedResources || {};
  const operator = Array.isArray(assigned.operators) ? assigned.operators[0] : null;
  const cabin = Array.isArray(assigned.cabins) ? assigned.cabins[0] : null;
  const preferredResources = appointment.preferredResources || {};
  const preferredOperatorIds = Array.isArray(preferredResources.operatorIds)
    ? preferredResources.operatorIds.map(Number).filter(Boolean)
    : [];
  const preferredCabinIds = Array.isArray(preferredResources.cabinIds)
    ? preferredResources.cabinIds.map(Number).filter(Boolean)
    : [];
  const assignedOperatorIds = Array.isArray(assigned.operators)
    ? assigned.operators.map((item) => Number(item.id)).filter(Boolean)
    : [];
  const assignedCabinIds = Array.isArray(assigned.cabins)
    ? assigned.cabins.map((item) => Number(item.id)).filter(Boolean)
    : [];
  const equipmentSelections = Array.isArray(assigned.equipment)
    ? assigned.equipment.map((item) => ({ equipmentId: Number(item.id), quantity: Number(item.quantity || 1) }))
    : [];
  const resourceCombination = {
    operatorIds: assignedOperatorIds.length ? assignedOperatorIds : preferredOperatorIds,
    cabinIds: assignedCabinIds.length ? assignedCabinIds : preferredCabinIds,
    equipment: equipmentSelections,
  };
  const pmsReference = appointment.pmsReference || {};
  const pmsSync = appointment.pmsSync || pmsReference.sync || pmsReference.syncStatus || "";
  const commercialStatus = appointment.commercialStatus || appointment.status || "OPTION";
  const addOns = Array.isArray(appointment.addOns)
    ? appointment.addOns.map((addOn) => ({
      ...addOn,
      addOnId: Number(addOn.addOnId ?? addOn.addonId),
      quantity: Number(addOn.quantity || 1),
      priceAdjustment: Number(addOn.priceAdjustment ?? addOn.priceDelta ?? 0),
      durationAdjustmentMinutes: Number(
        addOn.durationAdjustmentMinutes ?? addOn.durationDeltaMinutes ?? 0
      ),
    })).filter((addOn) => addOn.addOnId)
    : [];
  return {
    ...appointment,
    id: Number(appointment.id),
    date: start.date,
    startTime: start.time,
    endTime: end.time,
    clientId: appointment.client?.id ? Number(appointment.client.id) : "",
    clientName: displayPersonName(appointment.client, appointment.clientName || "-"),
    roomNumber: pmsReference.roomNumber || appointment.roomNumber || "",
    customerType: appointment.customerType || "HOTEL_GUEST",
    treatmentId: Number(appointment.treatment?.id ?? appointment.treatmentProductId ?? appointment.treatmentId ?? 0) || "",
    treatmentName: appointment.snapshot?.treatmentName || appointment.treatment?.name || "",
    operatorId: Number(operator?.id ?? preferredOperatorIds[0] ?? appointment.operatorId ?? 0) || "",
    resourceId: Number(cabin?.id ?? preferredCabinIds[0] ?? appointment.resourceId ?? 0) || "",
    equipmentSelections,
    resourceCombination,
    status: commercialStatus,
    commercialStatus,
    partySize: Number(appointment.partySize || 1),
    participantNames: (appointment.participants || []).map((participant) => (
      [participant.name, participant.surname].filter(Boolean).join(" ").trim()
    )).filter(Boolean).join("\n"),
    addOns,
    addonIds: addOns.map((addOn) => addOn.addOnId),
    basePrice: Number(appointment.basePrice ?? appointment.snapshot?.basePrice ?? 0),
    addOnTotal: Number(appointment.addOnTotal ?? appointment.snapshot?.addOnTotal ?? 0),
    finalPrice: Number(appointment.finalPrice ?? appointment.snapshot?.price ?? 0),
    baseDurationMinutes: Number(appointment.baseDurationMinutes || 0),
    addOnDurationMinutes: Number(appointment.addOnDurationMinutes || 0),
    finalDurationMinutes: Number(appointment.finalDurationMinutes || 0),
    requiredResources: appointment.requiredResources || {},
    bookingGroupId: appointment.bookingGroup?.id || "",
    pmsReservationId: pmsReference.reservationId || "",
    pmsBillId: pmsReference.billId || "",
    pmsGuestId: pmsReference.guestId || "",
    pmsReference,
    pmsSync,
    preferredResources,
    selectedCombination: null,
    statusReason: "",
  };
};

export const mapSpaPlannerAppointment = (appointment = {}) => {
  const start = toLocalDateParts(appointment.scheduledStartAt || appointment.startAt);
  const end = toLocalDateParts(appointment.scheduledEndAt || appointment.endAt);
  const resources = appointment.resources || {};
  const operators = Array.isArray(resources.operators) ? resources.operators : [];
  const cabins = Array.isArray(resources.cabins) ? resources.cabins : [];
  const equipmentItems = Array.isArray(resources.equipment) ? resources.equipment : [];
  const operatorIds = operators.map((item) => Number(item.id)).filter(Boolean);
  const cabinIds = cabins.map((item) => Number(item.id)).filter(Boolean);
  const equipmentSelections = equipmentItems
    .map((item) => ({ equipmentId: Number(item.id), quantity: Number(item.quantity || 1) }))
    .filter((item) => item.equipmentId && item.quantity > 0);
  const commercialStatus = appointment.commercialStatus || "OPTION";
  const operationalStatus = appointment.operationalStatus || "SCHEDULED";
  const participants = Array.isArray(appointment.participants) ? appointment.participants : [];
  const pmsReference = appointment.pmsReference || {};
  const pmsSync = appointment.pmsSync || pmsReference.sync || pmsReference.syncStatus || "";

  return {
    ...appointment,
    id: Number(appointment.id),
    date: start.date,
    startTime: start.time,
    endTime: end.time,
    clientId: appointment.customer?.id ? Number(appointment.customer.id) : "",
    clientName: displayPersonName(appointment.customer, appointment.clientName || "-"),
    roomNumber: pmsReference.roomNumber || appointment.hotelRoomNumber || appointment.roomNumber || "",
    customerType: appointment.customerType || "HOTEL_GUEST",
    treatmentId: Number(appointment.treatment?.id ?? appointment.treatmentProductId ?? 0) || "",
    treatmentName: appointment.treatment?.name || appointment.treatmentName || "",
    operatorId: operatorIds[0] || "",
    resourceId: cabinIds[0] || "",
    equipmentSelections,
    resourceCombination: {
      operatorIds,
      cabinIds,
      equipment: equipmentSelections,
    },
    assignedResources: {
      operators,
      cabins,
      equipment: equipmentItems,
      areas: Array.isArray(resources.areas) ? resources.areas : [],
    },
    status: commercialStatus,
    commercialStatus,
    operationalStatus,
    partySize: Number(appointment.partySize || 1),
    participantNames: participants.map((participant) => (
      displayPersonName(participant.client, [participant.name, participant.surname].filter(Boolean).join(" ").trim())
    )).filter((name) => name && name !== "-").join("\n"),
    execution: appointment.execution || null,
    addonIds: [],
    bookingGroupId: appointment.bookingGroupId || "",
    pmsReservationId: pmsReference.reservationId || "",
    pmsBillId: pmsReference.billId || "",
    pmsGuestId: pmsReference.guestId || "",
    pmsReference,
    pmsSync,
    preferredResources: {},
    selectedCombination: null,
    statusReason: "",
  };
};

const queryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const value = search.toString();
  return value ? `?${value}` : "";
};

export async function getSpaTreatments(token, tenant) {
  const data = await spaRequest("/treatments", token, tenant);
  return Array.isArray(data) ? data.map(mapSpaTreatment) : [];
}

export async function createSpaTreatment(treatment, token, tenant) {
  return mapSpaTreatment(await spaRequest("/treatments", token, tenant, {
    method: "POST",
    body: JSON.stringify(treatment),
  }));
}

export async function updateSpaTreatment(id, treatment, token, tenant) {
  const data = await spaRequest(`/treatments/${id}`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(treatment),
  });
  return mapSpaTreatment(data?.after || data);
}

export function getSpaTreatmentResources(id, token, tenant, options = {}) {
  return spaRequest(`/treatments/${id}/resources`, token, tenant, { onlineOnly: true, signal: options.signal });
}

export async function updateSpaTreatmentResources(id, resources, token, tenant) {
  const data = await spaRequest(`/treatments/${id}/resources`, token, tenant, {
    onlineOnly: true, method: 'PUT', body: JSON.stringify(resources),
  });
  return data?.after || data;
}

export async function getSpaOperators(token, tenant) {
  const data = await spaRequest("/operators", token, tenant);
  return Array.isArray(data) ? data.map(mapSpaOperator) : [];
}

export async function createSpaOperator(operator, token, tenant) {
  return mapSpaOperator(await spaRequest("/operators", token, tenant, {
    method: "POST",
    body: JSON.stringify(operator),
  }));
}

export async function updateSpaOperator(id, operator, token, tenant) {
  const data = await spaRequest(`/operators/${id}`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(operator),
  });
  return mapSpaOperator(data?.after || data);
}

export async function deactivateSpaOperator(id, token, tenant) {
  return mapSpaOperator(await spaRequest(`/operators/${id}`, token, tenant, {
    method: "DELETE",
  }));
}

export async function getSpaOperatorQualifications(operatorId, token, tenant) {
  const data = await spaRequest(`/operators/${operatorId}/qualifications`, token, tenant);
  return Array.isArray(data) ? data : [];
}

export async function addSpaOperatorQualification(operatorId, qualification, token, tenant) {
  return spaRequest(`/operators/${operatorId}/qualifications`, token, tenant, {
    method: "POST",
    body: JSON.stringify(qualification),
  });
}

export async function removeSpaOperatorQualification(operatorId, qualificationId, token, tenant) {
  return spaRequest(`/operators/${operatorId}/qualifications/${qualificationId}`, token, tenant, {
    method: "DELETE",
  });
}

export async function getSpaOperatorSchedule(operatorId, range, token, tenant) {
  const params = new URLSearchParams({ from: range.from, to: range.to });
  return spaRequest(`/operators/${operatorId}/schedule?${params.toString()}`, token, tenant);
}

export async function getSpaOperatorInvitations(operatorId, token, tenant) {
  const data = await spaRequest(`/operators/${operatorId}/invitations`, token, tenant, {
    onlineOnly: true,
  });
  return Array.isArray(data) ? data : [];
}

export async function createSpaOperatorInvitation(operatorId, payload, token, tenant) {
  return spaRequest(`/operators/${operatorId}/invitations`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
    onlineOnly: true,
  });
}

export async function resendSpaOperatorInvitation(operatorId, invitationId, payload, token, tenant) {
  return spaRequest(`/operators/${operatorId}/invitations/${invitationId}/remind`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
    onlineOnly: true,
  });
}

export async function revokeSpaOperatorInvitation(operatorId, invitationId, token, tenant) {
  return spaRequest(`/operators/${operatorId}/invitations/${invitationId}`, token, tenant, {
    method: "DELETE",
    onlineOnly: true,
  });
}

export async function respondSpaExternalInvitation(invitationToken, tenant, decision) {
  return spaRequest("/external/invitation/respond", invitationToken, tenant, {
    method: "POST",
    body: JSON.stringify({ decision }),
    onlineOnly: true,
  });
}

export async function getSpaExternalSchedule(range, invitationToken, tenant) {
  return spaRequest(`/external/schedule${queryString(range)}`, invitationToken, tenant, {
    onlineOnly: true,
  });
}

export async function createSpaOperatorScheduleEntry(operatorId, entry, token, tenant) {
  return spaRequest(`/operators/${operatorId}/schedule`, token, tenant, {
    method: "POST",
    body: JSON.stringify(entry),
  });
}

export async function updateSpaOperatorScheduleEntry(operatorId, entryId, entry, token, tenant) {
  const data = await spaRequest(`/operators/${operatorId}/schedule/${entryId}`, token, tenant, {
    method: "PUT",
    body: JSON.stringify(entry),
  });
  return data?.after || data;
}

export async function removeSpaOperatorScheduleEntry(operatorId, entryId, token, tenant) {
  return spaRequest(`/operators/${operatorId}/schedule/${entryId}`, token, tenant, {
    method: "DELETE",
  });
}

export async function getSpaCabins(token, tenant) {
  const data = await spaRequest("/cabins", token, tenant);
  return Array.isArray(data) ? data.map(mapSpaCabin) : [];
}

export async function createSpaCabin(cabin, token, tenant) {
  return mapSpaCabin(await spaRequest("/cabins", token, tenant, {
    method: "POST",
    body: JSON.stringify(cabin),
  }));
}

export async function updateSpaCabin(id, cabin, token, tenant) {
  return mapSpaCabin(await spaRequest(`/cabins/${id}`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(cabin),
  }));
}

export async function deactivateSpaCabin(id, token, tenant) {
  return mapSpaCabin(await spaRequest(`/cabins/${id}`, token, tenant, {
    method: "DELETE",
  }));
}

export async function getSpaCabinBlocks(id, range, token, tenant) {
  const data = await spaRequest(`/cabins/${id}/blocks${queryString(range)}`, token, tenant);
  return Array.isArray(data) ? data : [];
}

export async function createSpaCabinBlock(id, block, token, tenant) {
  return spaRequest(`/cabins/${id}/blocks`, token, tenant, {
    method: "POST",
    body: JSON.stringify(block),
  });
}

export async function updateSpaCabinBlock(id, blockId, block, token, tenant) {
  return spaRequest(`/cabins/${id}/blocks/${blockId}`, token, tenant, {
    method: "PUT",
    body: JSON.stringify(block),
  });
}

export async function removeSpaCabinBlock(id, blockId, token, tenant) {
  return spaRequest(`/cabins/${id}/blocks/${blockId}`, token, tenant, {
    method: "DELETE",
  });
}

export async function getSpaEquipment(token, tenant) {
  const data = await spaRequest("/equipment", token, tenant, { onlineOnly: true });
  if (!Array.isArray(data)) throw new Error('Invalid SPA equipment response');
  return data.map(mapSpaEquipment);
}

export async function getSpaInventoryProducts(token, tenant, options = {}) {
  const data = await spaRequest("/inventory/products", token, tenant, {
    onlineOnly: true,
    signal: options.signal,
  });
  if (!Array.isArray(data)) throw new Error("Invalid SPA inventory product response");
  return data.map((product) => ({
    ...product,
    id: Number(product.productId),
    productId: Number(product.productId),
    retailCategory: product.retailCategory || "OTHER",
    unitCost: Number(product.unitCost || 0),
    sellingPrice: Number(product.sellingPrice || 0),
  }));
}

export async function createSpaInventoryProduct(payload, token, tenant) {
  const product = await spaRequest("/inventory/products", token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(payload),
  });
  return {
    ...product,
    id: Number(product.productId),
    productId: Number(product.productId),
    retailCategory: product.retailCategory || "OTHER",
    unitCost: Number(product.unitCost || 0),
    sellingPrice: Number(product.sellingPrice || 0),
  };
}

export async function updateSpaInventoryProduct(productId, payload, token, tenant) {
  const response = await spaRequest(`/inventory/products/${productId}`, token, tenant, {
    onlineOnly: true,
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  const product = response?.after || response;
  return {
    ...product,
    id: Number(product.productId),
    productId: Number(product.productId),
    retailCategory: product.retailCategory || "OTHER",
    unitCost: Number(product.unitCost || 0),
    sellingPrice: Number(product.sellingPrice || 0),
  };
}

export async function getSpaInventoryLocations(token, tenant, options = {}) {
  const data = await spaRequest("/inventory/locations", token, tenant, {
    onlineOnly: true,
    signal: options.signal,
  });
  if (!Array.isArray(data)) throw new Error("Invalid SPA inventory location response");
  return data.map((location) => ({ ...location, id: Number(location.id) }));
}

export async function getSpaInventoryStock(filters, token, tenant, options = {}) {
  const data = await spaRequest(`/inventory/stock${queryString(filters)}`, token, tenant, {
    onlineOnly: true,
    signal: options.signal,
  });
  if (!Array.isArray(data)) throw new Error("Invalid SPA inventory stock response");
  return data.map((row) => ({
    ...row,
    quantity: Number(row.quantity || 0),
    location: row.location ? { ...row.location, id: Number(row.location.id) } : row.location,
    product: row.product ? {
      ...row.product,
      id: Number(row.product.id),
      unitCost: Number(row.product.unitCost || 0),
    } : row.product,
  }));
}

export async function getSpaRetailSales(filters, token, tenant, options = {}) {
  const data = await spaRequest(`/inventory/retail-sales${queryString(filters)}`, token, tenant, {
    onlineOnly: true,
    signal: options.signal,
  });
  if (!Array.isArray(data)) throw new Error("Invalid SPA retail sales response");
  return data;
}

export function createSpaRetailSale(payload, token, tenant) {
  return spaRequest("/inventory/retail-sales", token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getSpaEquipmentItem(id, token, tenant, options = {}) {
  return mapSpaEquipment(await spaRequest(`/equipment/${id}`, token, tenant, { onlineOnly: true, signal: options.signal }));
}

export async function deactivateSpaEquipment(id, token, tenant) {
  const data = await spaRequest(`/equipment/${id}`, token, tenant, { onlineOnly: true, method: 'DELETE' });
  return mapSpaEquipment(data?.after || data);
}

export function getSpaEquipmentBlocks(id, range, token, tenant, options = {}) {
  return spaRequest(`/equipment/${id}/blocks${queryString(range)}`, token, tenant, { onlineOnly: true, signal: options.signal });
}

export function createSpaEquipmentBlock(id, payload, token, tenant) {
  return spaRequest(`/equipment/${id}/blocks`, token, tenant, { onlineOnly: true, method: 'POST', body: JSON.stringify(payload) });
}

export function updateSpaEquipmentBlock(id, blockId, payload, token, tenant) {
  return spaRequest(`/equipment/${id}/blocks/${blockId}`, token, tenant, { onlineOnly: true, method: 'PUT', body: JSON.stringify(payload) });
}

export function removeSpaEquipmentBlock(id, blockId, token, tenant) {
  return spaRequest(`/equipment/${id}/blocks/${blockId}`, token, tenant, { onlineOnly: true, method: 'DELETE' });
}

export async function getSpaAppointments(token, tenant) {
  const data = await spaRequest("/appointments", token, tenant);
  return Array.isArray(data) ? data.map(mapSpaAppointment) : [];
}

export async function getSpaPlanner(filters = {}, token, tenant, options = {}) {
  const data = await spaRequest(`/planner${queryString(filters)}`, token, tenant, options);
  if (options.onlineOnly && !Array.isArray(data?.appointments)) throw new Error('Invalid SPA planner response');
  return {
    ...data,
    appointments: Array.isArray(data?.appointments) ? data.appointments.map(mapSpaPlannerAppointment) : [],
    notes: Array.isArray(data?.notes) ? data.notes : [],
  };
}

export async function createSpaPlannerNote(payload, token, tenant) {
  return spaRequest("/planner/notes", token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getSpaDashboard(date, token, tenant, options = {}) {
  const data = await spaRequest(`/dashboard${queryString({ date })}`, token, tenant, {
    onlineOnly: true, signal: options.signal,
  });
  const lists = ['arrivals', 'presentClients', 'waitingClients', 'inProgress', 'completed',
    'nextAppointments', 'cancellations', 'noShows', 'vipAndAlerts', 'absentOperators',
    'unavailableCabins', 'waitlist', 'notes'];
  const counters = ['arrivals', 'present', 'waiting', 'inProgress', 'completed',
    'nextAppointments', 'cancellations', 'noShows', 'activeWaitlist'];
  if (data?.date !== date || !data.timeZone || !data.from || !data.to
    || lists.some((key) => !Array.isArray(data[key]))
    || counters.some((key) => !Number.isFinite(data.summary?.[key]) || data.summary[key] < 0)) {
    throw new Error('Invalid SPA dashboard response');
  }
  return data;
}

export async function updateSpaPlannerNote(noteId, payload, token, tenant) {
  const data = await spaRequest(`/planner/notes/${noteId}`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data?.after || data;
}

export async function deleteSpaPlannerNote(noteId, token, tenant) {
  return spaRequest(`/planner/notes/${noteId}`, token, tenant, {
    method: "DELETE",
  });
}

export async function getSpaAppointment(id, token, tenant) {
  return mapSpaAppointment(await spaRequest(`/appointments/${id}`, token, tenant));
}

export async function getSpaClientProfile(clientId, token, tenant) {
  return spaRequest(`/clients/${clientId}/profile`, token, tenant);
}

export async function updateSpaClientProfile(clientId, payload, token, tenant) {
  return spaRequest(`/clients/${clientId}/profile`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getSpaClientAlerts(clientId, filters = {}, token, tenant) {
  const data = await spaRequest(`/clients/${clientId}/alerts${queryString(filters)}`, token, tenant);
  return Array.isArray(data) ? data : [];
}

export async function createSpaClientAlert(clientId, payload, token, tenant) {
  return spaRequest(`/clients/${clientId}/alerts`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateSpaClientAlert(clientId, alertId, payload, token, tenant) {
  return spaRequest(`/clients/${clientId}/alerts/${alertId}`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function checkSpaAppointmentAvailability(payload, token, tenant, options = {}) {
  return spaRequest("/appointments/availability", token, tenant, {
    onlineOnly: true,
    signal: options.signal,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createSpaAppointment(payload, token, tenant) {
  return mapSpaAppointment(await spaRequest("/appointments", token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(payload),
  }));
}

export async function updateSpaAppointmentAddOns(id, payload, token, tenant) {
  return mapSpaAppointment(await spaRequest(`/appointments/${id}`, token, tenant, {
    onlineOnly: true,
    method: "PATCH",
    body: JSON.stringify(payload),
  }));
}

const mapSpaAppointmentGroup = (group = {}) => ({
  ...group,
  bookingGroupId: Number(group.bookingGroupId || 0),
  appointments: Array.isArray(group.appointments)
    ? group.appointments.map(mapSpaAppointment)
    : [],
});

export async function createSpaAppointmentGroup(payload, token, tenant) {
  return mapSpaAppointmentGroup(await spaRequest("/appointment-groups", token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(payload),
  }));
}

export async function getSpaAppointmentGroup(groupId, token, tenant) {
  return mapSpaAppointmentGroup(await spaRequest(`/appointment-groups/${groupId}`, token, tenant));
}

export async function updateSpaAppointmentGroup(groupId, payload, token, tenant) {
  return mapSpaAppointmentGroup(await spaRequest(`/appointment-groups/${groupId}`, token, tenant, {
    onlineOnly: true,
    method: "PATCH",
    body: JSON.stringify(payload),
  }));
}

export async function cancelSpaAppointmentGroup(groupId, payload, token, tenant) {
  return spaRequest(`/appointment-groups/${groupId}/cancellation-fee`, token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function rescheduleSpaAppointment(id, payload, token, tenant) {
  return mapSpaAppointment(await spaRequest(`/appointments/${id}/reschedule`, token, tenant, {
    onlineOnly: true,
    method: "PATCH",
    body: JSON.stringify(payload),
  }));
}

export async function changeSpaAppointmentCommercialStatus(id, payload, token, tenant) {
  return mapSpaAppointment(await spaRequest(`/appointments/${id}/commercial-status`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(payload),
  }));
}

export async function getSpaAppointmentOrder(id, token, tenant) {
  return spaRequest(`/appointments/${id}/order`, token, tenant);
}

export async function createOrLinkSpaAppointmentOrder(id, payload = {}, token, tenant) {
  return spaRequest(`/appointments/${id}/order`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function checkoutSpaAppointment(id, payload, token, tenant) {
  return spaRequest(`/appointments/${id}/checkout`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function searchSpaPmsReservations(filters = {}, token, tenant) {
  return spaRequest(`/appointments/pms-reservations${queryString(filters)}`, token, tenant, {
    onlineOnly: true,
  });
}

export async function linkSpaAppointmentPmsReservation(id, payload, token, tenant) {
  return mapSpaAppointment(await spaRequest(`/appointments/${id}/pms-reservation`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(payload),
    onlineOnly: true,
  }));
}

export async function retrySpaAppointmentPmsSync(id, token, tenant) {
  return mapSpaAppointment(await spaRequest(`/appointments/${id}/pms-sync`, token, tenant, {
    method: "POST",
    onlineOnly: true,
  }));
}

export async function getSpaAppointmentPmsCharges(id, token, tenant) {
  const data = await spaRequest(`/appointments/${id}/pms-charges`, token, tenant, {
    onlineOnly: true,
  });
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.charges)) return data.charges;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export async function reverseSpaAppointmentPmsCharge(id, payload = {}, token, tenant) {
  return spaRequest(`/appointments/${id}/pms-charges/reverse`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
    onlineOnly: true,
  });
}

export async function getSpaCancellationFee(id, status, token, tenant) {
  return spaRequest(`/appointments/${id}/cancellation-fee${queryString({ status })}`, token, tenant);
}

export async function postSpaCancellationFee(id, payload, token, tenant) {
  return mapSpaAppointment(await spaRequest(`/appointments/${id}/cancellation-fee`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  }));
}

export async function checkInSpaAppointment(id, payload, token, tenant) {
  return mapSpaAppointment(await spaRequest(`/appointments/${id}/check-in`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(payload),
  }));
}

export async function getSpaTreatmentExecution(appointmentId, token, tenant) {
  return spaRequest(`/appointments/${appointmentId}/execution`, token, tenant);
}

export async function saveSpaTreatmentExecution(appointmentId, payload, token, tenant) {
  return spaRequest(`/appointments/${appointmentId}/execution`, token, tenant, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function completeSpaTreatmentExecution(appointmentId, payload = {}, token, tenant) {
  return spaRequest(`/appointments/${appointmentId}/execution/complete`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getSpaOperatorCalendar(operatorId, range, token, tenant) {
  return spaRequest(`/operators/${operatorId}/calendar${queryString(range)}`, token, tenant);
}

export async function getSpaCabinBoard(range, token, tenant, options = {}) {
  return spaRequest(`/cabins/board${queryString(range)}`, token, tenant, options);
}

export async function getSpaPackageEntitlements(filters = {}, token, tenant) {
  const data = await spaRequest(`/package-entitlements${queryString(filters)}`, token, tenant);
  return Array.isArray(data) ? data : [];
}

export async function createSpaPackageEntitlement(payload, token, tenant) {
  return spaRequest("/package-entitlements", token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function reserveSpaPackageCredit(itemId, payload, token, tenant) {
  return spaRequest(`/package-entitlements/items/${itemId}/reserve`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function consumeSpaPackageCredit(itemId, payload, token, tenant) {
  return spaRequest(`/package-entitlements/items/${itemId}/consume`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function releaseSpaPackageCredit(itemId, payload, token, tenant) {
  return spaRequest(`/package-entitlements/items/${itemId}/release`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function reverseSpaPackageTransaction(transactionId, payload, token, tenant) {
  return spaRequest(`/package-entitlements/transactions/${transactionId}/reverse`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function generateSpaPackageAppointments(entitlementId, payload, token, tenant) {
  return spaRequest(`/package-entitlements/${entitlementId}/appointments`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getSpaStoredValueInstruments(filters = {}, token, tenant) {
  const data = await spaRequest(`/stored-value-instruments${queryString(filters)}`, token, tenant, { onlineOnly: true });
  if (!Array.isArray(data)) throw new Error('Invalid SPA stored-value response');
  return data;
}

export async function createSpaStoredValueInstrument(payload, token, tenant) {
  return spaRequest("/stored-value-instruments", token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function reserveSpaStoredValue(instrumentId, payload, token, tenant) {
  return spaRequest(`/stored-value-instruments/${instrumentId}/reserve`, token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function consumeSpaStoredValue(instrumentId, payload, token, tenant) {
  return spaRequest(`/stored-value-instruments/${instrumentId}/consume`, token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function releaseSpaStoredValue(instrumentId, payload, token, tenant) {
  return spaRequest(`/stored-value-instruments/${instrumentId}/release`, token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function reverseSpaStoredValueTransaction(transactionId, payload, token, tenant) {
  return spaRequest(`/stored-value-instruments/transactions/${transactionId}/reverse`, token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function changeSpaStoredValueStatus(instrumentId, payload, token, tenant) {
  return spaRequest(`/stored-value-instruments/${instrumentId}/status`, token, tenant, {
    onlineOnly: true,
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getSpaTreatmentAddons(treatmentId, token, tenant, options = {}) {
  const data = await spaRequest(`/treatments/${treatmentId}/add-ons`, token, tenant);
  if (!Array.isArray(data)) return [];
  return options.includeInactive ? data : data.filter((addon) => addon.active !== false);
}

export async function createSpaTreatmentAddon(treatmentId, payload, token, tenant) {
  return spaRequest(`/treatments/${treatmentId}/add-ons`, token, tenant, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateSpaTreatmentAddon(treatmentId, addOnId, payload, token, tenant) {
  return spaRequest(`/treatments/${treatmentId}/add-ons/${addOnId}`, token, tenant, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deactivateSpaTreatmentAddon(treatmentId, addOnId, token, tenant) {
  return spaRequest(`/treatments/${treatmentId}/add-ons/${addOnId}/deactivate`, token, tenant, {
    method: "PATCH",
  });
}

export async function getSpaAreas(token, tenant) {
  const data = await spaRequest("/areas", token, tenant);
  return Array.isArray(data) ? data : [];
}

export async function createSpaEquipment(equipment, token, tenant) {
  return mapSpaEquipment(await spaRequest("/equipment", token, tenant, {
    onlineOnly: true,
    method: "POST",
    body: JSON.stringify(equipment),
  }));
}

export async function updateSpaEquipment(id, equipment, token, tenant) {
  const data = await spaRequest(`/equipment/${id}`, token, tenant, {
    onlineOnly: true,
    method: "PATCH",
    body: JSON.stringify(equipment),
  });
  return mapSpaEquipment(data?.after || data);
}

export const DEFAULT_SPA_SCHEDULING_SETTINGS = {
  timeZone: "Europe/Rome",
  openingHours: [
    { dayOfWeek: 1, start: "09:00", end: "18:00" },
    { dayOfWeek: 2, start: "09:00", end: "18:00" },
    { dayOfWeek: 3, start: "09:00", end: "18:00" },
    { dayOfWeek: 4, start: "09:00", end: "18:00" },
    { dayOfWeek: 5, start: "09:00", end: "18:00" },
    { dayOfWeek: 6, start: "09:00", end: "14:00" },
  ],
  closedDays: [0],
  holidays: [],
  plannerInterval: 15,
  appointmentsMayCrossMidnight: true,
};

export const SPA_STATUSES = [
  { value: "OPTION", label: "Option", color: "#64748B", background: "#F1F5F9" },
  { value: "CONFIRMED", label: "Confirmed", color: "#0B6E99", background: "#E8F6FB" },
  { value: "GUARANTEED", label: "Guaranteed", color: "#28745C", background: "#EAF7F1" },
  { value: "SCHEDULED", label: "Scheduled", color: "#455A64", background: "#EEF3F5" },
  { value: "ARRIVED", label: "Arrived", color: "#6B4CA5", background: "#F2ECFF" },
  { value: "WAITING", label: "Waiting", color: "#D47200", background: "#FFF4E4" },
  { value: "IN_PROGRESS", label: "In progress", color: "#9A5B00", background: "#FFF4E4" },
  { value: "COMPLETED", label: "Completed", color: "#166534", background: "#DCFCE7" },
  { value: "CANCELLED", label: "Cancelled", color: "#991B1B", background: "#FEE2E2" },
  { value: "NO_SHOW", label: "No-show", color: "#7F1D1D", background: "#FEE2E2" },
];

export const mockSpaEquipment = [
  { id: 1, name: "Hot stone kit", area: "Massage", quantity: 2, status: "available" },
  { id: 2, name: "Facial steamer", area: "Face", quantity: 1, status: "available" },
  { id: 3, name: "Aromatherapy diffuser", area: "Ritual", quantity: 3, status: "available" },
  { id: 4, name: "Thermal towels set", area: "Wellness", quantity: 20, status: "available" },
];

export const mockSpaAppointments = [
  {
    id: 1,
    date: "2026-09-21",
    startTime: "09:00",
    endTime: "09:50",
    clientName: "Anna Verdi",
    roomNumber: "204",
    customerType: "hotel_guest",
    treatmentId: 1,
    operatorId: 1,
    resourceId: 1,
    status: "CONFIRMED",
    notes: "Prefers medium pressure",
  },
  {
    id: 2,
    date: "2026-09-21",
    startTime: "10:30",
    endTime: "11:50",
    clientName: "Luca Ferri",
    roomNumber: "118",
    customerType: "hotel_guest",
    treatmentId: 2,
    operatorId: 3,
    resourceId: 3,
    status: "GUARANTEED",
    notes: "",
  },
  {
    id: 3,
    date: "2026-09-21",
    startTime: "12:00",
    endTime: "12:45",
    clientName: "Maria Leone",
    roomNumber: "",
    customerType: "external",
    treatmentId: 3,
    operatorId: 3,
    resourceId: 2,
    status: "ARRIVED",
    notes: "Allergy check required",
  },
  {
    id: 4,
    date: "2026-09-22",
    startTime: "15:00",
    endTime: "16:00",
    clientName: "Paolo Neri",
    roomNumber: "310",
    customerType: "hotel_guest",
    treatmentId: 4,
    operatorId: 2,
    resourceId: 4,
    status: "OPTION",
    notes: "",
  },
];

export async function getSpaPlannerData() {
  await wait();
  return {
    appointments: mockSpaAppointments,
  };
}

export async function getSpaCatalogData() {
  await wait();
  return {
    equipment: mockSpaEquipment,
  };
}

export async function getSpaSchedulingSettings(token, tenant) {
  const response = await safeFetch("/api/spa/settings/scheduling", {
    method: "GET",
    headers: spaAuthHeaders(token, tenant),
    cache: "no-store",
  });

  if (!response.ok) {
    const errorPayload = await readErrorPayload(response);
    const error = new Error(errorPayload.message);
    error.status = response.status;
    error.rejections = errorPayload.rejections;
    error.payload = errorPayload.payload;
    throw error;
  }

  return response.json();
}

export async function updateSpaSchedulingSettings(settings, token, tenant) {
  const response = await safeFetch("/api/spa/settings/scheduling", {
    method: "PATCH",
    headers: spaAuthHeaders(token, tenant),
    body: JSON.stringify(settings),
    cache: "no-store",
  });

  if (!response.ok) {
    const errorPayload = await readErrorPayload(response);
    const error = new Error(errorPayload.message);
    error.status = response.status;
    error.rejections = errorPayload.rejections;
    error.payload = errorPayload.payload;
    throw error;
  }

  return response.json();
}

export async function getSpaCancellationPolicy(token, tenant) {
  return spaRequest("/settings/cancellation-policy", token, tenant);
}

export async function updateSpaCancellationPolicy(policy, token, tenant) {
  return spaRequest("/settings/cancellation-policy", token, tenant, {
    method: "PATCH",
    body: JSON.stringify(policy),
  });
}

export const SPA_DIRECT_PAYMENT_METHODS = ["CASH", "CARD", "BANK_TRANSFER", "INVOICE", "ROOM_CHARGE"];
